import React, { useState, useEffect } from 'react';
import { Award, TrendingUp, Clock, Plus, History, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const MeusObjetivosTab = () => {
  const [objetivos, setObjetivos] = useState({ ativos: [], historico: [] });
  const [loading, setLoading] = useState(true);
  const [showRegistroModal, setShowRegistroModal] = useState(false);
  const [objetivoSelecionado, setObjetivoSelecionado] = useState(null);
  const [showHistorico, setShowHistorico] = useState(false);
  
  const [registroData, setRegistroData] = useState({
    valor: '',
    observacao: ''
  });

  useEffect(() => {
    loadObjetivos();
  }, []);

  const loadObjetivos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/objetivos-pontuais/meus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setObjetivos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar objetivos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRegistro = (objetivo) => {
    setObjetivoSelecionado(objetivo);
    setShowRegistroModal(true);
    setRegistroData({ valor: '', observacao: '' });
  };

  const handleRegistrarProgresso = async (e) => {
    e.preventDefault();
    
    if (!registroData.valor || parseFloat(registroData.valor) <= 0) {
      toast.error('Valor deve ser maior que zero');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/objetivos-pontuais/${objetivoSelecionado.id}/registrar-progresso`,
        {
          valor: parseFloat(registroData.valor),
          observacao: registroData.observacao || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowRegistroModal(false);
      setObjetivoSelecionado(null);
      setRegistroData({ valor: '', observacao: '' });
      loadObjetivos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar progresso');
    }
  };

  const getPercentualConcluido = (objetivo) => {
    return ((objetivo.progresso_atual || 0) / objetivo.meta_valor * 100).toFixed(1);
  };

  if (loading) return <div className="text-center py-8">Carregando...</div>;

  return (
    <div className="space-y-6">
      {/* Header com toggle */}
      <Card className="bg-gradient-card border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Award className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Meus Objetivos</h2>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={loadObjetivos}
                size="sm"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
              <Button
                variant={!showHistorico ? 'default' : 'outline'}
                onClick={() => setShowHistorico(false)}
                size="sm"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Ativos ({objetivos.ativos.length})
              </Button>
              <Button
                variant={showHistorico ? 'default' : 'outline'}
                onClick={() => setShowHistorico(true)}
                size="sm"
              >
                <History className="h-4 w-4 mr-2" />
                Histórico ({objetivos.historico.length})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Objetivos Ativos */}
      {!showHistorico && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {objetivos.ativos.length === 0 ? (
            <Card className="md:col-span-2">
              <CardContent className="p-8 text-center text-muted-foreground">
                <Award className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum objetivo ativo no momento</p>
              </CardContent>
            </Card>
          ) : (
            objetivos.ativos.map((objetivo) => (
              <Card key={objetivo.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Award className="h-5 w-5 text-primary" />
                    {objetivo.titulo}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {objetivo.descricao && (
                    <p className="text-sm text-muted-foreground">{objetivo.descricao}</p>
                  )}
                  
                  <div className="space-y-2 text-sm">
                    {objetivo.seccao && (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Secção:</span>
                        <span>{objetivo.seccao}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span className="font-semibold">Válido até:</span>
                      <span>{new Date(objetivo.data_fim).toLocaleDateString('pt-PT')}</span>
                    </div>
                    
                    <div className="p-3 bg-primary/5 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">Meta:</span>
                        <span className="text-lg font-bold">
                          {objetivo.tipo_meta === 'valor' 
                            ? `€${objetivo.meta_valor.toFixed(2)}`
                            : `${objetivo.meta_valor} ${objetivo.unidade_meta || 'unidades'}`}
                        </span>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span>Progresso atual:</span>
                          <span className="text-primary font-bold">
                            {objetivo.tipo_meta === 'valor' 
                              ? `€${(objetivo.progresso_atual || 0).toFixed(2)}`
                              : `${objetivo.progresso_atual || 0} ${objetivo.unidade_meta || 'unidades'}`}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-primary h-3 rounded-full transition-all"
                            style={{ 
                              width: `${Math.min(getPercentualConcluido(objetivo), 100)}%` 
                            }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground text-right">
                          {getPercentualConcluido(objetivo)}% concluído
                        </p>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-success/10 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-success">Recompensa ao concluir:</span>
                        <span className="text-lg font-bold text-success">
                          {objetivo.tipo_recompensa === 'dinheiro' 
                            ? `€${objetivo.valor_recompensa.toFixed(2)}`
                            : `${objetivo.valor_recompensa} pontos`}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleOpenRegistro(objetivo)}
                    className="w-full bg-primary hover:bg-primary/90"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Registrar Progresso
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Lista de Histórico */}
      {showHistorico && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {objetivos.historico.length === 0 ? (
            <Card className="md:col-span-2">
              <CardContent className="p-8 text-center text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum objetivo no histórico</p>
              </CardContent>
            </Card>
          ) : (
            objetivos.historico.map((objetivo) => (
              <Card key={objetivo.id} className="opacity-75">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Award className="h-5 w-5" />
                      {objetivo.titulo}
                    </CardTitle>
                    <Badge variant={objetivo.status === 'concluido' ? 'default' : 'outline'}>
                      {objetivo.status === 'concluido' ? 'Concluído' : 'Expirado'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>
                    <span className="font-semibold">Meta: </span>
                    {objetivo.tipo_meta === 'valor' 
                      ? `€${objetivo.meta_valor.toFixed(2)}`
                      : `${objetivo.meta_valor} ${objetivo.unidade_meta || 'unidades'}`}
                  </div>
                  <div>
                    <span className="font-semibold">Atingido: </span>
                    {objetivo.tipo_meta === 'valor' 
                      ? `€${(objetivo.progresso_atual || 0).toFixed(2)}`
                      : `${objetivo.progresso_atual || 0} ${objetivo.unidade_meta || 'unidades'}`}
                    {' '}({getPercentualConcluido(objetivo)}%)
                  </div>
                  {objetivo.status === 'concluido' && (
                    <div className="p-2 bg-success/10 rounded text-success font-semibold">
                      Recompensa: {objetivo.tipo_recompensa === 'dinheiro' 
                        ? `€${objetivo.valor_recompensa.toFixed(2)}`
                        : `${objetivo.valor_recompensa} pontos`}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Modal de Registro */}
      {showRegistroModal && objetivoSelecionado && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Registrar Progresso</h2>
              <p className="text-sm text-muted-foreground mt-1">{objetivoSelecionado.titulo}</p>
            </div>
            
            <form onSubmit={handleRegistrarProgresso} className="p-6 space-y-4">
              <div>
                <Label htmlFor="valor">
                  Valor * {objetivoSelecionado.tipo_meta === 'valor' ? '(€)' : `(${objetivoSelecionado.unidade_meta || 'unidades'})`}
                </Label>
                <Input
                  id="valor"
                  type="text"
                  step={objetivoSelecionado.tipo_meta === 'valor' ? '0.01' : '1'}
                  value={registroData.valor}
                  onChange={(e) => setRegistroData({ ...registroData, valor: e.target.value })}
                  placeholder={objetivoSelecionado.tipo_meta === 'valor' ? '50.00' : '10'}
                  required
                  autoFocus
                />
              </div>

              <div>
                <Label htmlFor="observacao">Observação (opcional)</Label>
                <textarea
                  id="observacao"
                  value={registroData.observacao}
                  onChange={(e) => setRegistroData({ ...registroData, observacao: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  rows={3}
                  placeholder="Ex: Vendas da manhã..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowRegistroModal(false);
                    setObjetivoSelecionado(null);
                    setRegistroData({ valor: '', observacao: '' });
                  }}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Registrar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeusObjetivosTab;
